import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const FILTER_ITEM_SELECTOR = '[data-product-media-filter-item]';
const MAIN_ITEM_SELECTOR = '[data-product-media-main-item]';
const MEDIA_GALLERY_SELECTOR = 'media-gallery[data-product-media-filtering]';
const VARIANT_EVENT_NAMES = new Set([ThemeEvents.variantUpdate, 'variant:change', 'product:variant-change']);
const VARIANT_PREVIEW_EVENT = 'variant:preview';
const VARIANT_PREVIEW_RESET_EVENT = 'variant:preview-reset';
const SECTION_LOAD_EVENT_NAMES = ['section:load', 'shopify:section:load'];
const PREVIEW_DURATION = 160;
const PREVIEW_STYLE_ID = 'aimi-media-gallery-preview-style';

function ensurePreviewStyles() {
  if (document.getElementById(PREVIEW_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = PREVIEW_STYLE_ID;
  style.textContent = `
    .media-gallery__variant-preview-overlay {
      position: absolute;
      inset: 0;
      z-index: 3;
      overflow: hidden;
      pointer-events: none;
      opacity: 0;
      transition: opacity ${PREVIEW_DURATION}ms cubic-bezier(.22,.61,.36,1);
      background: var(--color-background);
    }

    .media-gallery__variant-preview-overlay.is-visible {
      opacity: 1;
    }

    .media-gallery__variant-preview-overlay img {
      display: block;
      width: 100%;
      height: 100%;
    }
  `;
  document.head.append(style);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
const toId = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return String(value);
};

/**
 * @param {string | undefined} value
 * @returns {string[]}
 */
const splitIds = (value) => {
  if (!value) return [];
  return value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

/**
 * Normalize color-like strings so "Soft Beige", "Soft_Beige", and "soft-beige" compare the same.
 * @param {unknown} value
 * @returns {string}
 */
const normalizeColor = (value) =>
  toId(value)
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '');

/**
 * Media alts often include suffixes such as Pink_01 or Black_Model.
 * Matching by prefix keeps all media owned by the selected color together.
 * @param {HTMLElement} item
 * @param {string} selectedColor
 * @returns {boolean}
 */
const mediaMatchesSelectedColor = (item, selectedColor) => {
  if (!selectedColor) return false;

  const mediaColor = normalizeColor(item.dataset.color);

  return Boolean(mediaColor && (mediaColor === selectedColor || mediaColor.startsWith(selectedColor)));
};

/**
 * @param {HTMLElement} item
 * @returns {string}
 */
const getMediaId = (item) => toId(item.dataset.mediaId);

/**
 * @param {HTMLElement} item
 * @returns {string[]}
 */
const getMediaVariantIds = (item) => splitIds(item.dataset.variantIds || item.dataset.mediaVariantIds);

/**
 * @param {HTMLElement} item
 * @returns {boolean}
 */
const isAttachedToVariant = (item) => {
  if (item.dataset.attached === 'true') return true;
  if (item.dataset.attached === 'false') return false;
  return getMediaVariantIds(item).length > 0;
};

/**
 * @param {Element[]} items
 * @returns {HTMLElement[]}
 */
const uniqueMediaItems = (items) => {
  const seen = new Set();
  const uniqueItems = [];

  for (const item of items) {
    if (!(item instanceof HTMLElement)) continue;

    const mediaId = getMediaId(item);
    if (!mediaId || seen.has(mediaId)) continue;

    seen.add(mediaId);
    uniqueItems.push(item);
  }

  return uniqueItems;
};

/**
 * @param {HTMLElement} gallery
 * @returns {HTMLElement[]}
 */
const getRepresentativeMediaItems = (gallery) => {
  const mainItems = Array.from(gallery.querySelectorAll(`${MAIN_ITEM_SELECTOR}${FILTER_ITEM_SELECTOR}`));
  const filterItems = Array.from(gallery.querySelectorAll(FILTER_ITEM_SELECTOR));

  return uniqueMediaItems(mainItems.length ? mainItems : filterItems);
};

/**
 * @param {HTMLElement[]} representativeItems
 * @param {string} selectedColor
 * @param {string} variantId
 * @param {string} featuredMediaId
 * @returns {Set<string>}
 */
const getAllowedMediaIds = (representativeItems, selectedColor, variantId, featuredMediaId) => {
  const allMediaIds = representativeItems.map(getMediaId).filter(Boolean);

  if (selectedColor) {
    const colorMediaIds = new Set(
      representativeItems
        .filter((item) => mediaMatchesSelectedColor(item, selectedColor))
        .map(getMediaId)
        .filter(Boolean)
    );

    if (colorMediaIds.size) return colorMediaIds;
  }

  if (!variantId) return new Set(allMediaIds);

  const variantMediaIds = new Set(
    representativeItems
      .filter((item) => getMediaVariantIds(item).includes(variantId))
      .map(getMediaId)
      .filter(Boolean)
  );

  if (variantMediaIds.size) return variantMediaIds;

  const fallbackMediaIds = new Set(
    representativeItems
      .filter((item) => {
        const mediaId = getMediaId(item);
        return mediaId === featuredMediaId || !isAttachedToVariant(item);
      })
      .map(getMediaId)
      .filter(Boolean)
  );

  if (!fallbackMediaIds.size) return new Set(allMediaIds);

  return fallbackMediaIds;
};

/**
 * @param {HTMLElement} item
 */
const pauseHiddenMedia = (item) => {
  item.querySelectorAll('deferred-media, product-model').forEach((media) => {
    if ('pauseMedia' in media && typeof media.pauseMedia === 'function') {
      media.pauseMedia();
    }
  });

  item.querySelectorAll('video').forEach((video) => video.pause());

  item.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
    if (!(iframe instanceof HTMLIFrameElement)) return;

    iframe.contentWindow?.postMessage(
      iframe.dataset.videoType === 'youtube'
        ? '{"event":"command","func":"pauseVideo","args":""}'
        : '{"method":"pause"}',
      '*'
    );
  });
};

/**
 * @param {HTMLElement} gallery
 * @param {Set<string>} allowedMediaIds
 */
const applyMediaVisibility = (gallery, allowedMediaIds) => {
  const filterItems = Array.from(gallery.querySelectorAll(FILTER_ITEM_SELECTOR));

  for (const item of filterItems) {
    if (!(item instanceof HTMLElement)) continue;

    const mediaId = getMediaId(item);
    const shouldHide = Boolean(mediaId && !allowedMediaIds.has(mediaId));

    item.hidden = shouldHide;

    if (shouldHide) {
      pauseHiddenMedia(item);
    }
  }
};

/**
 * @param {HTMLElement} gallery
 * @returns {HTMLElement[]}
 */
const getSlides = (gallery) =>
  Array.from(gallery.querySelectorAll(`slideshow-slide${FILTER_ITEM_SELECTOR}`)).filter(
    (slide) => slide instanceof HTMLElement
  );

/**
 * @param {HTMLElement} gallery
 * @param {Set<string>} allowedMediaIds
 * @returns {string}
 */
const getCurrentVisibleMediaId = (gallery, allowedMediaIds) => {
  const currentSlide = getSlides(gallery).find(
    (slide) => !slide.hidden && slide.getAttribute('aria-hidden') === 'false'
  );
  const currentMediaId = currentSlide ? getMediaId(currentSlide) : '';

  return currentMediaId && allowedMediaIds.has(currentMediaId) ? currentMediaId : '';
};

/**
 * @param {HTMLElement[]} representativeItems
 * @param {Set<string>} allowedMediaIds
 * @param {string} featuredMediaId
 * @param {string} currentMediaId
 * @returns {string}
 */
const getTargetMediaId = (representativeItems, allowedMediaIds, featuredMediaId, currentMediaId) => {
  if (featuredMediaId && allowedMediaIds.has(featuredMediaId)) return featuredMediaId;
  if (currentMediaId && allowedMediaIds.has(currentMediaId)) return currentMediaId;

  const targetItem = representativeItems.find((item) => allowedMediaIds.has(getMediaId(item)));

  return targetItem ? getMediaId(targetItem) : '';
};

/**
 * @param {HTMLElement} gallery
 * @param {HTMLElement[]} slides
 * @param {string} targetMediaId
 */
const selectVisibleSlide = (gallery, slides, targetMediaId) => {
  const targetSlide =
    slides.find((slide) => !slide.hidden && targetMediaId && getMediaId(slide) === targetMediaId) ||
    slides.find((slide) => !slide.hidden);

  if (!targetSlide) return;

  const slideshow = gallery.querySelector('slideshow-component');

  if (slideshow instanceof HTMLElement && 'select' in slideshow && typeof slideshow.select === 'function') {
    // slideshow.select(index) indexes into the slideshow's VISIBLE slides only
    // (it internally ignores [hidden] slides). Variant color filtering hides
    // slides, so an index taken from the full slide list no longer lines up.
    // Compute the index against the same visible-only list the slideshow uses.
    const visibleSlides = slides.filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
    const targetIndex = visibleSlides.indexOf(targetSlide);

    if (targetIndex !== -1) {
      slideshow.select(targetIndex, undefined, { animate: false });
    } else {
      targetSlide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  } else {
    targetSlide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

/**
 * @param {HTMLElement} gallery
 * @param {HTMLElement[]} slides
 * @param {string} targetMediaId
 */
const syncMobileThumbnails = (gallery, slides, targetMediaId) => {
  const visibleSlideCount = slides.filter((slide) => !slide.hidden).length;
  const controls = gallery.querySelectorAll('.media-gallery__mobile-controls');

  controls.forEach((control) => {
    if (control instanceof HTMLElement) {
      control.hidden = visibleSlideCount <= 1;
    }
  });

  const thumbnails = gallery.querySelectorAll('.media-gallery__mobile-controls .slideshow-controls__thumbnail');

  thumbnails.forEach((thumbnail, index) => {
    const control = thumbnail.closest('button, [role="button"], slideshow-control') || thumbnail;
    const slide = slides[index];

    if (!(control instanceof HTMLElement)) return;

    const shouldHide = !slide || slide.hidden;
    control.hidden = shouldHide;
    control.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');

    if (!shouldHide && slide && targetMediaId && getMediaId(slide) === targetMediaId) {
      control.setAttribute('aria-current', 'true');
    } else {
      control.removeAttribute('aria-current');
    }
  });
};

/**
 * @param {HTMLElement} gallery
 * @param {string} targetMediaId
 */
const syncZoomDialog = (gallery, targetMediaId) => {
  const thumbnails = Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')).filter(
    (thumbnail) => thumbnail instanceof HTMLElement
  );

  thumbnails.forEach((thumbnail) => {
    if (!(thumbnail instanceof HTMLElement)) return;

    if (!thumbnail.hidden && targetMediaId && getMediaId(thumbnail) === targetMediaId) {
      thumbnail.setAttribute('aria-selected', 'true');
      thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } else {
      thumbnail.removeAttribute('aria-selected');
    }
  });

  if (!targetMediaId) return;

  const dialog = gallery.querySelector('zoom-dialog dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  const targetZoomMedia = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery [data-media-id]')).find(
    (item) => item instanceof HTMLElement && !item.hidden && getMediaId(item) === targetMediaId
  );

  if (targetZoomMedia instanceof HTMLElement) {
    targetZoomMedia.scrollIntoView({ block: 'nearest', inline: 'start' });
  }
};

/**
 * @param {HTMLElement} gallery
 * @param {string} targetMediaId
 */
const updateTransitionMedia = (gallery, targetMediaId) => {
  if (gallery.dataset.productTransitionEnabled !== 'true') return;

  const mainItems = gallery.querySelectorAll(MAIN_ITEM_SELECTOR);

  mainItems.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    item.removeAttribute('data-view-transition-type');

    if (!item.hidden && targetMediaId && getMediaId(item) === targetMediaId) {
      item.setAttribute('data-view-transition-type', 'product-image-transition');
    }
  });
};

/**
 * @param {unknown} variant
 * @returns {string | undefined}
 */
const getFeaturedMediaIdFromVariant = (variant) => {
  if (!variant || typeof variant !== 'object') return undefined;

  const candidate = /** @type {Record<string, any>} */ (variant);
  const featuredMedia = candidate.featured_media || candidate.featuredMedia || candidate.featured_image;

  return (
    toId(featuredMedia?.id) ||
    toId(candidate.featured_media_id) ||
    toId(candidate.featuredMediaId) ||
    undefined
  );
};

/**
 * @param {unknown} variant
 * @returns {string}
 */
const getColorFromVariant = (variant) => {
  if (!variant || typeof variant !== 'object') return '';

  const candidate = /** @type {Record<string, any>} */ (variant);

  return normalizeColor(candidate.option1 || candidate.options?.[0] || candidate.color || candidate.colour);
};

/**
 * @param {HTMLElement} gallery
 * @returns {string}
 */
const getSelectedColorFromControls = (gallery) => {
  const scope = gallery.closest('.shopify-section, dialog, product-card') || document;
  const picker = scope.querySelector('variant-picker');

  if (!picker) return '';

  const selectedInput = picker.querySelector('fieldset:first-of-type input:checked');
  if (selectedInput instanceof HTMLInputElement) {
    return normalizeColor(selectedInput.value || selectedInput.dataset.optionValue || selectedInput.getAttribute('aria-label'));
  }

  const selectedOption = picker.querySelector('select:first-of-type option:checked, select:first-of-type option[selected]');
  if (selectedOption instanceof HTMLOptionElement) {
    return normalizeColor(selectedOption.textContent || selectedOption.value);
  }

  return '';
};

/**
 * @param {HTMLElement} gallery
 * @returns {Record<string, any> | null}
 */
const getScopedVariant = (gallery) => {
  const scope = gallery.closest('.shopify-section, dialog, product-card') || document;
  const script = scope.querySelector('variant-picker script[type="application/json"]');
  const text = script?.textContent?.trim();

  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (_error) {
    return null;
  }
};

/**
 * @returns {string}
 */
const getVariantIdFromUrl = () => new URL(window.location.href).searchParams.get('variant') || '';

/**
 * @param {HTMLElement} gallery
 * @param {Record<string, any>} options
 * @returns {{ variantId: string, featuredMediaId: string, selectedColor: string }}
 */
const getVariantState = (gallery, options) => {
  const optionVariant = options.variant || options.resource || null;
  const hasExplicitVariantId = options.variantId !== undefined || options.variant_id !== undefined;
  const scopedVariant = optionVariant || (hasExplicitVariantId ? null : getScopedVariant(gallery));
  const rawVariantId = options.variantId ?? options.variant_id ?? scopedVariant?.id;
  const hasIncomingVariant = rawVariantId !== undefined && rawVariantId !== null && rawVariantId !== '';
  const variantId = hasIncomingVariant
    ? toId(rawVariantId)
    : gallery.dataset.selectedVariantId || getVariantIdFromUrl() || '';
  const rawFeaturedMediaId =
    options.featuredMediaId ??
    options.featured_media_id ??
    options.featuredMedia?.id ??
    options.featured_media?.id ??
    getFeaturedMediaIdFromVariant(scopedVariant);
  const featuredMediaId =
    rawFeaturedMediaId !== undefined && rawFeaturedMediaId !== null && rawFeaturedMediaId !== ''
      ? toId(rawFeaturedMediaId)
      : hasIncomingVariant
        ? ''
        : gallery.dataset.selectedVariantFeaturedMediaId || '';
  const selectedColor =
    normalizeColor(options.selectedColor ?? options.color ?? options.option1) ||
    getColorFromVariant(scopedVariant) ||
    getSelectedColorFromControls(gallery) ||
    normalizeColor(gallery.dataset.selectedColor);

  return { variantId, featuredMediaId, selectedColor };
};

/**
 * Filters a product media gallery to the selected variant color.
 *
 * @param {HTMLElement} gallery
 * @param {Record<string, any>} [options]
 */
export function filterGalleryByVariant(gallery, options = {}) {
  if (!(gallery instanceof HTMLElement)) return;

  const normalizedOptions =
    options && typeof options === 'object' && 'id' in options && !('variantId' in options)
      ? { variant: options }
      : options;
  const { variantId, featuredMediaId, selectedColor } = getVariantState(gallery, normalizedOptions);
  const representativeItems = getRepresentativeMediaItems(gallery);

  if (!representativeItems.length) return;

  const allowedMediaIds = getAllowedMediaIds(representativeItems, selectedColor, variantId, featuredMediaId);
  const currentMediaId = getCurrentVisibleMediaId(gallery, allowedMediaIds);
  const targetMediaId = getTargetMediaId(representativeItems, allowedMediaIds, featuredMediaId, currentMediaId);

  gallery.dataset.selectedVariantId = variantId;

  if (selectedColor) {
    gallery.dataset.selectedColor = selectedColor;
  } else {
    delete gallery.dataset.selectedColor;
  }

  if (featuredMediaId) {
    gallery.dataset.selectedVariantFeaturedMediaId = featuredMediaId;
  } else {
    delete gallery.dataset.selectedVariantFeaturedMediaId;
  }

  if (targetMediaId) {
    gallery.dataset.selectedMediaId = targetMediaId;
  } else {
    delete gallery.dataset.selectedMediaId;
  }

  applyMediaVisibility(gallery, allowedMediaIds);

  const slides = getSlides(gallery);

  syncMobileThumbnails(gallery, slides, targetMediaId);
  selectVisibleSlide(gallery, slides, targetMediaId);
  syncZoomDialog(gallery, targetMediaId);
  updateTransitionMedia(gallery, targetMediaId);

  gallery.dispatchEvent(
    new CustomEvent('aimi:media-gallery-updated', {
      bubbles: true,
      detail: { targetMediaId },
    })
  );
}

export const updateVariantGallery = filterGalleryByVariant;

/**
 * @param {Event} event
 * @returns {Record<string, any> | null}
 */
const getVariantFromEvent = (event) => {
  const detail = 'detail' in event ? event.detail || {} : {};
  const data = detail.data || {};

  return detail.variant || detail.resource || detail.selectedVariant || data.variant || data.resource || null;
};

/**
 * @param {Event} event
 * @param {Record<string, any> | null} variant
 * @returns {string}
 */
const getVariantIdFromEvent = (event, variant) => {
  const detail = 'detail' in event ? event.detail || {} : {};
  const data = detail.data || {};

  return (
    toId(variant?.id) ||
    toId(detail.variantId) ||
    toId(detail.variant_id) ||
    toId(detail.id) ||
    toId(data.variantId) ||
    toId(data.variant_id) ||
    toId(data.id)
  );
};

/**
 * @param {Event} event
 * @param {Record<string, any> | null} variant
 * @returns {string}
 */
const getFeaturedMediaIdFromEvent = (event, variant) => {
  const detail = 'detail' in event ? event.detail || {} : {};
  const data = detail.data || {};
  const featuredMedia =
    variant?.featured_media ||
    variant?.featuredMedia ||
    detail.featured_media ||
    detail.featuredMedia ||
    data.featured_media ||
    data.featuredMedia;

  return (
    toId(featuredMedia?.id) ||
    toId(variant?.featured_media_id) ||
    toId(variant?.featuredMediaId) ||
    toId(detail.featured_media_id) ||
    toId(detail.featuredMediaId) ||
    toId(data.featured_media_id) ||
    toId(data.featuredMediaId)
  );
};

/**
 * @param {Event} event
 * @returns {string}
 */
const getEventProductId = (event) => {
  const detail = 'detail' in event ? event.detail || {} : {};
  const data = detail.data || {};
  const target = event.target instanceof Element ? event.target : null;

  return (
    toId(data.newProduct?.id) ||
    toId(data.productId) ||
    toId(detail.productId) ||
    toId(detail.product_id) ||
    target?.closest('[data-product-id]')?.dataset.productId ||
    ''
  );
};

/**
 * @param {Event} event
 * @param {HTMLElement} gallery
 * @returns {HTMLElement | null}
 */
const getRenderedMediaGallery = (event, gallery) => {
  const detail = 'detail' in event ? event.detail || {} : {};
  const html = detail.data?.html;

  if (!html || !('querySelectorAll' in html)) return null;

  const renderedGalleries = Array.from(html.querySelectorAll(MEDIA_GALLERY_SELECTOR));
  const productId = gallery.dataset.productId || '';

  return (
    renderedGalleries.find(
      (renderedGallery) =>
        renderedGallery instanceof HTMLElement &&
        (!productId || !renderedGallery.dataset.productId || renderedGallery.dataset.productId === productId)
    ) || null
  );
};

/**
 * @param {Event} event
 * @returns {string}
 */
const getVariantColorFromChangeEvent = (event) => {
  const target = event.target;

  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return '';

  const picker = target.closest('variant-picker');
  const firstFieldset = picker?.querySelector('fieldset:first-of-type');
  const firstSelect = picker?.querySelector('select:first-of-type');

  if (target instanceof HTMLInputElement && firstFieldset?.contains(target)) {
    return normalizeColor(target.value || target.dataset.optionValue || target.getAttribute('aria-label'));
  }

  if (target instanceof HTMLSelectElement && target === firstSelect) {
    const selectedOption = target.options[target.selectedIndex];
    return normalizeColor(selectedOption?.textContent || selectedOption?.value);
  }

  return '';
};

const getVariantIdFromChangeEvent = (event) => {
  const target = event.target;

  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return '';

  if (target.name === 'id' && target.value) return target.value;

  if (target instanceof HTMLSelectElement) {
    const selectedOption = target.options[target.selectedIndex];
    return toId(selectedOption?.dataset.variantId) || toId(selectedOption?.value);
  }

  return toId(target.dataset.variantId) || toId(target.closest('form')?.querySelector('input[name="id"]')?.value);
};

if (typeof window !== 'undefined') {
  window.ProductVariantMediaFiltering = {
    ...(window.ProductVariantMediaFiltering || {}),
    updateVariantGallery: filterGalleryByVariant,
    filterGalleryByVariant,
  };
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {HTMLElement | null} */
  #previewOverlay = null;

  /** @type {number | null} */
  #previewTimer = null;

  connectedCallback() {
    super.connectedCallback();
    ensurePreviewStyles();

    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    VARIANT_EVENT_NAMES.forEach((eventName) => {
      document.addEventListener(eventName, this.#handleVariantEvent, { capture: true, signal });
    });

    document.addEventListener('change', this.#handleChange, { capture: true, signal });
    document.addEventListener(VARIANT_PREVIEW_EVENT, this.#handleVariantPreview, { capture: true, signal });
    document.addEventListener(VARIANT_PREVIEW_RESET_EVENT, this.#handleVariantPreviewReset, { capture: true, signal });

    SECTION_LOAD_EVENT_NAMES.forEach((eventName) => {
      document.addEventListener(eventName, this.#handleSectionLoad, { signal });
    });

    document.addEventListener('DOMContentLoaded', this.#handleSectionLoad, { signal });
    window.addEventListener('popstate', this.#handleHistoryChange, { signal });
    window.addEventListener('pageshow', this.#handleHistoryChange, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => this.updateVariantGallery());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
    this.#clearPreviewOverlay();
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #eventAppliesToGallery(event) {
    const productId = getEventProductId(event);

    if (productId && this.dataset.productId && productId !== this.dataset.productId) {
      const detail = 'detail' in event ? event.detail || {} : {};
      if (!detail.data?.newProduct) return false;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return true;

    const galleryScope = this.closest('.shopify-section, dialog, product-card');
    const targetScope = target.closest('.shopify-section, dialog, product-card');

    return !galleryScope || !targetScope || galleryScope === targetScope || galleryScope.contains(target);
  }

  /**
   * Handles variant change/update events without rebuilding the gallery for the same product.
   *
   * @param {VariantUpdateEvent | Event} event - The variant event.
   */
  #handleVariantEvent = (event) => {
    if (!this.#eventAppliesToGallery(event)) return;

    this.#clearPreviewOverlay();

    const variant = getVariantFromEvent(event);
    const variantId = getVariantIdFromEvent(event, variant);
    const renderedGallery = getRenderedMediaGallery(event, this);
    const renderedProductId = renderedGallery?.dataset.productId || '';

    if (renderedGallery && renderedProductId && this.dataset.productId && renderedProductId !== this.dataset.productId) {
      this.replaceWith(renderedGallery);
      return;
    }

    const featuredMediaId =
      getFeaturedMediaIdFromEvent(event, variant) || renderedGallery?.dataset.selectedVariantFeaturedMediaId || '';
    const nextVariantId = variantId || renderedGallery?.dataset.selectedVariantId || '';

    if (!nextVariantId && !featuredMediaId) return;

    this.updateVariantGallery({ variant, variantId: nextVariantId, featuredMediaId, selectedColor: getColorFromVariant(variant) });
  };

  /**
   * Handles fallback native change events from variant controls.
   *
   * @param {Event} event
   */
  #handleChange = (event) => {
    if (!this.#eventAppliesToGallery(event)) return;

    this.#clearPreviewOverlay();

    const variantId = getVariantIdFromChangeEvent(event);
    const selectedColor = getVariantColorFromChangeEvent(event);
    if (!variantId && !selectedColor) return;

    requestAnimationFrame(() => this.updateVariantGallery({ variantId, selectedColor }));
  };

  /**
   * Handles section rendering in AJAX/theme editor flows.
   *
   * @param {Event} event
   */
  #handleSectionLoad = (event) => {
    if (event.target instanceof Element && !event.target.contains(this)) return;

    requestAnimationFrame(() => this.updateVariantGallery());
  };

  #handleHistoryChange = () => {
    requestAnimationFrame(() => {
      const variantId = getVariantIdFromUrl();

      if (variantId) {
        this.updateVariantGallery({ variantId });
      } else {
        this.updateVariantGallery();
      }
    });
  };

  /**
   * Handles non-locking swatch media previews.
   * @param {Event} event
   */
  #handleVariantPreview = (event) => {
    if (!this.#eventAppliesToGallery(event)) return;

    const detail = 'detail' in event ? event.detail || {} : {};
    const mediaId = toId(detail.mediaId);

    if (!mediaId) return;

    this.previewMedia(mediaId);
  };

  #handleVariantPreviewReset = (event) => {
    if (!this.#eventAppliesToGallery(event)) return;

    this.#clearPreviewOverlay();
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Updates the gallery to the selected variant media.
   *
   * @param {Record<string, any>} [options]
   */
  updateVariantGallery(options = {}) {
    filterGalleryByVariant(this, options);
  }

  /**
   * Previews a target media item with a quiet crossfade.
   * @param {string} mediaId
   */
  previewMedia(mediaId) {
    const targetItem = Array.from(this.querySelectorAll(FILTER_ITEM_SELECTOR)).find(
      (item) => item instanceof HTMLElement && getMediaId(item) === mediaId
    );
    const targetImage = targetItem?.querySelector('img');

    if (!(targetItem instanceof HTMLElement) || !(targetImage instanceof HTMLImageElement)) return;

    const mobileFrame = this.querySelector('.media-gallery__mobile-slider');
    const firstVisibleMainItem = Array.from(this.querySelectorAll(MAIN_ITEM_SELECTOR)).find(
      (item) => item instanceof HTMLElement && !item.hidden
    );
    const frame =
      window.matchMedia('(max-width: 749px)').matches && mobileFrame
        ? mobileFrame
        : firstVisibleMainItem || mobileFrame || this;

    if (!(frame instanceof HTMLElement)) return;

    const computedPosition = getComputedStyle(frame).position;
    if (computedPosition === 'static') frame.style.position = 'relative';

    this.#clearPreviewOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'media-gallery__variant-preview-overlay';

    const image = targetImage.cloneNode(false);
    if (image instanceof HTMLImageElement) {
      image.removeAttribute('loading');
      image.decoding = 'async';
      image.style.objectFit = getComputedStyle(targetImage).objectFit || 'cover';
      image.style.objectPosition = getComputedStyle(targetImage).objectPosition || 'center center';
      overlay.append(image);
    }

    frame.append(overlay);
    this.#previewOverlay = overlay;

    const slides = getSlides(this);
    if (slides.length) {
      selectVisibleSlide(this, slides, mediaId);
    }

    requestAnimationFrame(() => overlay.classList.add('is-visible'));

    this.#previewTimer = window.setTimeout(() => {
      this.#clearPreviewOverlay();
    }, PREVIEW_DURATION + 40);
  }

  #clearPreviewOverlay() {
    if (this.#previewTimer !== null) {
      window.clearTimeout(this.#previewTimer);
      this.#previewTimer = null;
    }

    this.#previewOverlay?.remove();
    this.#previewOverlay = null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
